import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { useDealerManagementSimple } from '@/hooks/useDealerManagementSimple';
import { TechnicianDropdown } from '../components/TechnicianDropdown';
import { RecallAssignmentList, SelectedRecall } from '../components/RecallAssignmentList';
import { ManualStopData } from '../components/ManualStopForm';
import {
  DuplicateLocationDialog,
  DuplicateLocationChoice,
  NearbyStopInfo
} from '../components/DuplicateLocationDialog';
import {
  createTechnicianAssignment,
  deleteTechnicianAssignment
} from '@/lib/supabase/technicianAssignments';
import { RoutePreviewPanel } from '../components/RoutePreviewPanel';
import { printDealerRecallSummary } from '../utils/printRoutes';
import { useAutoSaveAssignment } from '../hooks/useAutoSaveAssignment';
import { deleteRouteByTechnicianDate } from '@/lib/supabase/technicianRoutes';
import { useRoute, routeKeys } from '../hooks/useTechnicianRoutes';
import { useToast } from '@/hooks/use-toast';
import { describeActionError } from '@/lib/errors/lifecycleRefusal';
import { isRecallProcessed } from '@/lib/supabase/assignmentStatus';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { Loader2, Eye } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { format } from 'date-fns';
import { logUserActivity } from '@/lib/activity/userActivity';
import { logUserActivityDebounced, cancelDebouncedLog } from '@/lib/activity/debouncedActivity';
import { useRecallAssignmentStatus } from '@/hooks/useRecallAssignmentStatus';
import { TechnicianRoute } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Parse recall key back to vin and campaign
const parseRecallKey = (key: string): { vin: string; campaignNumber: string } => {
  const [vin, campaignNumber] = key.split('|');
  return { vin: vin || '', campaignNumber: campaignNumber || '' };
};

/**
 * Unified Routing Page
 *
 * Combines assignment and route preview into a single interface:
 * - Technician dropdown at top
 * - Recall list on left (55%)
 * - Route preview map on right (45%)
 * - Bulk assignment bar at bottom
 *
 * Features:
 * - Auto-save on assignment
 * - Progressive filtering (only unassigned recalls shown by default)
 * - Live route preview updates
 * - Bulk selection and assignment
 */
// eslint-disable-next-line max-lines-per-function
const UnifiedRoutingPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuth();
  const userId = user?.id;
  const tenantId = useCurrentTenantId();
  const canAssign = hasPermission({ resource: 'recalls', action: 'assign_technicians' });

  // Data hooks
  const {
    selectedRecalls,
    loading: recallsLoading,
    removeRecall,
    stagedManualStops
  } = useSelectedRecalls();

  const {
    assignments,
    activeTechnicians,
    loading: assignmentsLoading,
    refreshData: refreshAssignments,
    deleteAssignment,
    syncToRouteTable
  } = useTechnicianAssignments();

  useDealerManagementSimple(userId);

  // Get assignment status for filtering manual stops
  const { getRecallStatus, completedManualStops } = useRecallAssignmentStatus();

  // Get tenant coordinates for route starting point
  const [tenantLocation, setTenantLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [tenantName, setTenantName] = useState<string>('');

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{
    type: 'unassign' | 'remove';
    assignmentId?: string;
    vin?: string;
    campaignNumber?: string;
  } | null>(null);

  // Fetch tenant information for coordinates using the tenant context
  useEffect(() => {
    const fetchTenantLocation = async () => {
      if (!tenantId) {
        return;
      }

      try {
        const prodClient = getProdSupabaseClient();

        const { data: tenant, error } = await prodClient
          .from('tenants')
          .select('tenant_name, latitude, longitude')
          .eq('id', tenantId)
          .single();

        if (error) {
          return;
        }

        if (tenant?.tenant_name) {
          setTenantName(tenant.tenant_name);
        }

        if (
          tenant?.latitude &&
          tenant?.longitude &&
          !isNaN(tenant.latitude) &&
          !isNaN(tenant.longitude)
        ) {
          setTenantLocation({ lat: tenant.latitude, lng: tenant.longitude });
        }
      } catch {
        // Failed to fetch tenant location - non-critical, use defaults
      }
    };

    fetchTenantLocation();
  }, [tenantId]);

  // Log page view on mount
  useEffect(() => {
    logUserActivity({
      category: 'routing',
      action: 'view_manual_routing'
    });

    // Cleanup debounced logs on unmount
    return () => {
      cancelDebouncedLog('technician-select');
      cancelDebouncedLog('date-select');
    };
  }, []);

  // Local state
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date()); // Default to today
  const [filterMode, setFilterMode] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [selectedRecallKeys, setSelectedRecallKeys] = useState<Set<string>>(new Set());
  // Duplicate location detection
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [nearbyStopInfo, setNearbyStopInfo] = useState<NearbyStopInfo | null>(null);
  const [pendingManualStop, setPendingManualStop] = useState<ManualStopData | null>(null);

  // Auto-select first technician when data loads
  useEffect(() => {
    if (selectedTechnicianId === null && activeTechnicians.length > 0) {
      // Sort alphabetically (same as TechnicianDropdown) and pick first active
      const sortedTechnicians = [...activeTechnicians]
        .filter(tech => tech.is_active)
        .sort((a, b) => a.full_name.localeCompare(b.full_name));

      const firstTechnician = sortedTechnicians[0];
      if (firstTechnician) {
        setSelectedTechnicianId(firstTechnician.id);
      }
    }
  }, [activeTechnicians, selectedTechnicianId]);

  // Fetch stored route for selected technician + date (single source of truth for assignments)
  const { data: storedRoute, refetch: refetchRoute } = useRoute({
    technicianId: selectedTechnicianId,
    routeDate: selectedDate,
    enabled: !!selectedTechnicianId
  });

  // Per-technician + per-date custom start location state (local cache, synced from database)
  // Key: "technicianId-YYYY-MM-DD", Value: { mode, customLocation }
  // Each technician can have a different start location for each day
  const [technicianStartLocations, setTechnicianStartLocations] = useState<
    Map<
      string,
      {
        mode: 'default' | 'custom';
        customLocation: { lat: number; lng: number; displayName: string } | null;
      }
    >
  >(new Map());

  // Print route state - receives route data from RoutePreviewPanel
  const [currentRoute, setCurrentRoute] = useState<TechnicianRoute | null>(null);

  // Create a unique key for technician + date combination
  const getLocationKey = useCallback((techId: string, date: Date): string => {
    return `${techId}-${format(date, 'yyyy-MM-dd')}`;
  }, []);

  // Restore custom location from stored route when route data loads (single source of truth)
  // Only restores custom locations - default mode is already the initial state
  useEffect(() => {
    if (!selectedTechnicianId || !storedRoute?.route_data?.start_location) {
      return;
    }

    const startLocation = storedRoute.route_data.start_location;
    const key = getLocationKey(selectedTechnicianId, selectedDate);

    // Only restore if we have a custom location stored in the route
    if (startLocation.type === 'custom' && startLocation.lat && startLocation.lng) {
      setTechnicianStartLocations(prev => {
        const existing = prev.get(key);
        // Skip if already set with same data to avoid infinite loops
        if (
          existing?.mode === 'custom' &&
          existing?.customLocation?.lat === startLocation.lat &&
          existing?.customLocation?.lng === startLocation.lng
        ) {
          return prev;
        }
        const newMap = new Map(prev);
        newMap.set(key, {
          mode: 'custom',
          customLocation: {
            lat: startLocation.lat,
            lng: startLocation.lng,
            displayName: startLocation.display_name || startLocation.address || ''
          }
        });
        return newMap;
      });
    }
    // Note: We don't reset to default here - that would overwrite pending user changes.
    // The local state defaults to 'default' mode, which is correct initial behavior.
  }, [selectedTechnicianId, selectedDate, storedRoute, getLocationKey]);

  // Get current technician + date's start location settings (defaults to 'default' mode with tenant address)
  const currentTechLocationSettings = useMemo(() => {
    if (!selectedTechnicianId) {
      return { mode: 'default' as const, customLocation: null };
    }
    const key = getLocationKey(selectedTechnicianId, selectedDate);
    return technicianStartLocations.get(key) || { mode: 'default' as const, customLocation: null };
  }, [selectedTechnicianId, selectedDate, technicianStartLocations, getLocationKey]);

  // Compare current UI state to saved route state to detect if start location has changed
  const isStartLocationDirty = useMemo(() => {
    const saved = storedRoute?.route_data?.start_location;
    const currentMode = currentTechLocationSettings.mode;
    const currentCustom = currentTechLocationSettings.customLocation;

    // No saved route - dirty if custom location set
    if (!saved) {
      return currentMode === 'custom' && currentCustom !== null;
    }

    // Compare types
    const currentType = currentMode === 'custom' ? 'custom' : 'tenant_default';
    if (saved.type !== currentType) return true;

    // If both custom, compare coordinates
    if (currentType === 'custom' && currentCustom) {
      const latDiff = Math.abs((saved.lat || 0) - currentCustom.lat);
      const lngDiff = Math.abs((saved.lng || 0) - currentCustom.lng);
      return latDiff > 0.0001 || lngDiff > 0.0001;
    }

    return false;
  }, [storedRoute, currentTechLocationSettings]);

  // Handlers to update current technician + date's start location (local state only)
  // The start location is persisted to the database when the assign button is pressed
  const handleLocationModeChange = useCallback(
    (mode: 'default' | 'custom') => {
      if (!selectedTechnicianId) return;
      const key = getLocationKey(selectedTechnicianId, selectedDate);
      setTechnicianStartLocations(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(key) || { mode: 'default', customLocation: null };
        newMap.set(key, { ...current, mode });
        return newMap;
      });
    },
    [selectedTechnicianId, selectedDate, getLocationKey]
  );

  const handleCustomLocationChange = useCallback(
    (location: { lat: number; lng: number; displayName: string } | null) => {
      if (!selectedTechnicianId) return;
      const key = getLocationKey(selectedTechnicianId, selectedDate);
      setTechnicianStartLocations(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(key) || { mode: 'default', customLocation: null };
        newMap.set(key, { ...current, customLocation: location });
        return newMap;
      });
    },
    [selectedTechnicianId, selectedDate, getLocationKey]
  );

  // Handle route changes from RoutePreviewPanel
  const handleRouteChange = useCallback((route: TechnicianRoute | null) => {
    setCurrentRoute(route);
  }, []);

  // Auto-save hook
  const { isAssigning, bulkAssignRecalls, clearRecentAssignments } = useAutoSaveAssignment(() => {
    // Refresh data after assignment - both assignments and route
    refreshAssignments();
    refetchRoute();
  });

  // Get selected technician object
  const selectedTechnician = useMemo(() => {
    if (!selectedTechnicianId) return null;
    return activeTechnicians.find(t => t.id === selectedTechnicianId) || null;
  }, [selectedTechnicianId, activeTechnicians]);

  // Handle print route - prints directly without dialog
  const handlePrintRoute = useCallback(() => {
    if (currentRoute && currentRoute.stops.length > 0) {
      printDealerRecallSummary(currentRoute);
    }
  }, [currentRoute]);

  // Derive assigned recalls from stored route (single source of truth)
  const assignmentRecalls = useMemo((): SelectedRecall[] => {
    if (!storedRoute?.route_data?.stops) {
      return [];
    }

    // Extract all recalls from route stops
    const recalls: SelectedRecall[] = [];
    storedRoute.route_data.stops.forEach(stop => {
      stop.recalls.forEach(recall => {
        if (recall.stop_type === 'manual') {
          return;
        }
        // Skip work that is already done. route_data carries a status snapshot, and a
        // route rebuild pulls finished recalls back into a new route — so without this,
        // completed and cancelled recalls re-enter the assignment list.
        if (isRecallProcessed(recall.status)) {
          return;
        }
        const recallData: SelectedRecall = {
          vin: recall.vin,
          campaign_number: recall.campaign_number,
          recall_title: recall.recall_title,
          dealer_name: stop.dealer_name,
          dealer_address: stop.dealer_address,
          dealer_city: stop.dealer_city,
          dealer_region: stop.dealer_region,
          dealer_latitude: stop.lat,
          dealer_longitude: stop.lng,
          ...(stop.dealer_postal_code !== undefined && {
            dealer_postal_code: stop.dealer_postal_code
          }),
          ...(stop.dealer_phone !== undefined && { dealer_phone: stop.dealer_phone })
        };
        // Only add optional fields if they have values
        if (recall.vehicle_year != null) {
          recallData.year = recall.vehicle_year;
        }
        if (recall.vehicle_make) {
          recallData.make = recall.vehicle_make;
        }
        if (recall.vehicle_model) {
          recallData.model = recall.vehicle_model;
        }
        if (recall.stock_number) {
          recallData.stock_number = recall.stock_number;
        }
        recalls.push(recallData);
      });
    });

    return recalls;
  }, [storedRoute]);

  // Filter out recalls that have been processed (confirmed, completed, or cancelled)
  // Once a recall's status is updated in route tracker, it should not appear in the assignment page
  const unprocessedRecalls = useMemo(() => {
    return selectedRecalls.filter(recall => {
      const existingAssignment = assignments.find(
        a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
      );

      if (!existingAssignment) {
        // No assignment exists, show the recall
        return true;
      }

      const status = existingAssignment.assignment_status;
      // Exclude: confirmed, completed, cancelled (these have been processed)
      // Include: assigned, in_progress (these can still be reassigned)
      return status !== 'confirmed' && status !== 'completed' && status !== 'cancelled';
    });
  }, [selectedRecalls, assignments]);

  // Transform selected recalls and merge with assignment recalls + staged manual stops
  const recallsForList = useMemo((): SelectedRecall[] => {
    // Filter out manual stops from unprocessedRecalls (they're handled separately below)
    const recallsWithoutManualStops = unprocessedRecalls.filter(r => !r.is_manual_stop);

    const fromSelected: SelectedRecall[] = recallsWithoutManualStops.map(recall => ({
      vin: recall.vin,
      campaign_number: recall.campaign_number,
      recall_title: recall.recall_title || recall.nhtsa_campaign_number,
      dealer_name: recall.dealer_name || recall.seller_name,
      dealer_address: recall.dealer_address || recall.dealer_street,
      dealer_city: recall.dealer_city,
      dealer_region: recall.dealer_region,
      dealer_postal_code: recall.dealer_postal_code,
      dealer_latitude: recall.dealer_latitude,
      dealer_longitude: recall.dealer_longitude,
      dealer_phone: recall.dealer_phone,
      // Map database field names to expected format
      year: recall.car_year || recall.year || recall.model_year,
      make: recall.make_name || recall.make,
      model: recall.model_name || recall.model,
      stock_number: recall.stock_number
    }));

    // Merge in route recalls that are not already listed.
    //
    // This merge and `unprocessedRecalls` used to fight each other, and the merge won.
    // `unprocessedRecalls` correctly drops a completed recall from `fromSelected`, which
    // removes its key from `existingKeys` — so the merge then saw it as "not already in
    // the list" and added it straight back. The better the filter got, the more finished
    // work the merge restored, and it rendered as UNASSIGNED because
    // RecallAssignmentList's assignment map also excludes completed rows. A dispatcher
    // was being invited to re-assign work that was already done.
    //
    // Check the live assignment rather than the JSONB snapshot: the web never writes
    // recall status back to route_data on completion, so the snapshot can lag.
    const existingKeys = new Set(fromSelected.map(r => `${r.vin}|${r.campaign_number}`));
    const additional = assignmentRecalls.filter(recall => {
      if (existingKeys.has(`${recall.vin}|${recall.campaign_number}`)) {
        return false;
      }
      const liveAssignment = assignments.find(
        a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
      );
      return !isRecallProcessed(liveAssignment?.assignment_status);
    });

    // Filter staged manual stops to exclude assigned and completed ones
    // Only show manual stops that are available for assignment
    const unassignedManualStops = stagedManualStops.filter(stop => {
      const key = `${stop.vin}|${stop.campaign_number}`;
      // Exclude completed manual stops
      if (completedManualStops.has(key)) return false;
      // Exclude assigned manual stops (they already have an assignment)
      const status = getRecallStatus(stop.vin, stop.campaign_number);
      if (status.status === 'assigned' || status.status === 'confirmed') return false;
      return true;
    });

    // Transform staged manual stops to SelectedRecall format
    const fromStagedManualStops: SelectedRecall[] = unassignedManualStops.map(stop => ({
      // Use the stored VIN or generate a key from the ID
      vin: stop.vin || `MANUAL-${stop.id}`,
      campaign_number: stop.campaign_number || 'MANUAL',
      recall_title: stop.recall_title || stop.manual_stop_reason,
      dealer_name: stop.dealer_name,
      dealer_address: stop.dealer_address,
      dealer_city: stop.dealer_city,
      dealer_region: stop.dealer_region,
      dealer_postal_code: stop.dealer_postal_code,
      dealer_latitude: stop.dealer_latitude,
      dealer_longitude: stop.dealer_longitude,
      dealer_phone: stop.dealer_phone,
      year: stop.vehicle_year,
      make: stop.vehicle_make,
      model: stop.vehicle_model,
      // Manual stop specific fields
      is_manual_stop: true,
      stop_type: 'manual' as const,
      manual_stop_id: stop.id,
      manual_stop_reason: stop.manual_stop_reason,
      manual_stop_notes: stop.manual_stop_notes,
      // Customer-stop fields, for display and household grouping only. They are NOT sent
      // when the stop is assigned — a DB trigger copies them onto the assignment.
      ...(stop.stop_kind && { stop_kind: stop.stop_kind }),
      ...(stop.contact_id && { contact_id: stop.contact_id }),
      ...(stop.address_line2 && { address_line2: stop.address_line2 }),
      ...(stop.dispatcher_note && { dispatcher_note: stop.dispatcher_note })
    }));

    return [...fromSelected, ...additional, ...fromStagedManualStops];
  }, [
    unprocessedRecalls,
    assignmentRecalls,
    assignments,
    stagedManualStops,
    completedManualStops,
    getRecallStatus
  ]);

  // Handle technician change
  const handleTechnicianChange = useCallback(
    (technicianId: string) => {
      setSelectedTechnicianId(technicianId);
      clearRecentAssignments();
      // Clear selection when changing technicians - let user select what they want
      setSelectedRecallKeys(new Set());

      // Log technician selection (debounced to avoid logging rapid changes)
      const techName = activeTechnicians.find(t => t.id === technicianId)?.full_name;
      logUserActivityDebounced(
        {
          category: 'routing',
          action: 'technician_selected',
          entityType: 'technician',
          entityId: technicianId,
          ...(techName && { entityName: techName }),
          metadata: { technician_id: technicianId, technician_name: techName }
        },
        'technician-select'
      );
    },
    [clearRecentAssignments, activeTechnicians]
  );

  // Track if this is the initial render (don't log initial date)
  const isInitialDateRef = useRef(true);

  // Clear selection when date changes and log activity
  useEffect(() => {
    setSelectedRecallKeys(new Set());

    // Skip logging initial date value
    if (isInitialDateRef.current) {
      isInitialDateRef.current = false;
      return;
    }

    // Log date selection (debounced to avoid logging rapid changes)
    logUserActivityDebounced(
      {
        category: 'routing',
        action: 'date_selected',
        metadata: { selected_date: format(selectedDate, 'yyyy-MM-dd') }
      },
      'date-select'
    );
  }, [selectedDate]);

  // Handle recall selection change
  const handleSelectionChange = useCallback((keys: Set<string>) => {
    setSelectedRecallKeys(keys);
  }, []);

  // Handle unassign (remove technician assignment) - shows confirmation dialog
  const handleUnassign = useCallback((assignmentId: string) => {
    setConfirmAction({ type: 'unassign', assignmentId });
  }, []);

  // Handle remove recall (remove from selected recalls) - shows confirmation dialog
  const handleRemoveRecall = useCallback((vin: string, campaignNumber: string) => {
    setConfirmAction({ type: 'remove', vin, campaignNumber });
  }, []);

  // Execute the confirmed action (unassign or remove)
  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);

    if (action.type === 'unassign' && action.assignmentId) {
      try {
        const assignment = assignments.find(a => a.id === action.assignmentId);
        if (assignment) {
          setSelectedRecallKeys(prev => {
            const newSet = new Set(prev);
            const key = `${assignment.vin}|${assignment.campaign_number}`;
            newSet.delete(key);
            return newSet;
          });
        }

        await deleteAssignment(action.assignmentId);
        await refetchRoute();

        toast({
          title: 'Assignment Removed',
          description: 'The recall has been unassigned from technician',
          variant: 'destructive'
        });
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to remove assignment',
          variant: 'destructive'
        });
      }
    } else if (action.type === 'remove' && action.vin && action.campaignNumber) {
      try {
        const assignment = assignments.find(
          a => a.vin === action.vin && a.campaign_number === action.campaignNumber
        );
        const technicianId = assignment?.technician_id;
        const expectedVisitDate = assignment?.expected_visit_date;

        if (assignment) {
          await deleteAssignment(assignment.id);
        }

        await removeRecall(action.vin, action.campaignNumber);

        setSelectedRecallKeys(prev => {
          const newSet = new Set(prev);
          const key = `${action.vin}|${action.campaignNumber}`;
          newSet.delete(key);
          return newSet;
        });

        // Ensure route is rebuilt after removal (handles case where
        // removeSelectedRecall deletes assignment without route sync,
        // e.g. last recall at a dealer stop must be removed,
        // remaining stops re-optimized, and saved to technician_routes)
        if (technicianId && expectedVisitDate) {
          await syncToRouteTable(technicianId, expectedVisitDate);
        }
        await refetchRoute();

        queryClient.invalidateQueries({ queryKey: routeKeys.all });

        toast({
          title: 'Recall Removed',
          description: assignment
            ? 'The recall has been unassigned and removed from your route'
            : 'The recall has been removed from your list',
          variant: 'destructive'
        });
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to remove recall',
          variant: 'destructive'
        });
      }
    }
  }, [
    confirmAction,
    assignments,
    deleteAssignment,
    refetchRoute,
    toast,
    removeRecall,
    syncToRouteTable,
    queryClient
  ]);

  // Handle clear all recalls from selected technician (unassign all)
  const handleClearAllFromTech = useCallback(async () => {
    if (!selectedTechnicianId) return;

    // Get all assignments for the selected technician
    const techAssignments = assignments.filter(
      a =>
        a.technician_id === selectedTechnicianId &&
        a.assignment_status !== 'completed' &&
        a.assignment_status !== 'cancelled'
    );

    if (techAssignments.length === 0) {
      toast({
        title: 'No Assignments',
        description: 'No recalls assigned to this technician',
        variant: 'default'
      });
      return;
    }

    try {
      // Collect unique dates for route sync
      const uniqueDates = new Set<string>();
      techAssignments.forEach(a => {
        if (a.expected_visit_date) {
          uniqueDates.add(a.expected_visit_date);
        }
      });

      // Delete all assignments for this technician
      await Promise.all(techAssignments.map(a => deleteTechnicianAssignment(a.id)));

      // Delete routes for each affected date
      for (const dateStr of uniqueDates) {
        try {
          await deleteRouteByTechnicianDate(selectedTechnicianId, dateStr);
        } catch (deleteError) {
          console.warn('Failed to delete route after clearing assignments:', deleteError);
        }
      }

      // Clear selection
      setSelectedRecallKeys(new Set());

      // Refresh data
      await refreshAssignments();
      await refetchRoute();

      // Invalidate all route queries so other pages get fresh data
      queryClient.invalidateQueries({ queryKey: routeKeys.all });

      const count = techAssignments.length;
      toast({
        title: 'All Recalls Cleared',
        description: `${count} recall${count !== 1 ? 's' : ''} unassigned from technician`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to clear some assignments',
        variant: 'destructive'
      });
    }
  }, [selectedTechnicianId, assignments, refreshAssignments, refetchRoute, toast, queryClient]);

  // Handle clear selected unassigned recalls (remove from route entirely)
  const handleClearSelectedUnassigned = useCallback(async () => {
    // Get selected keys that are genuinely unassigned — no assignment row at all.
    //
    // This used to ask "is there an assignment that is not completed or cancelled?",
    // which classified a FINISHED recall as unassigned and queued it for removal. That is
    // how completed work reached the delete path in the first place. Finished work is
    // neither assigned nor unassigned: it is done, and belongs in neither bucket.
    const unassignedKeys: string[] = [];
    selectedRecallKeys.forEach(key => {
      const [vin, campaignNumber] = key.split('|');
      const assignment = assignments.find(
        a => a.vin === vin && a.campaign_number === campaignNumber
      );
      if (!assignment) {
        unassignedKeys.push(key);
      }
    });

    if (unassignedKeys.length === 0) {
      toast({
        title: 'No Unassigned Recalls',
        description: 'No unassigned recalls are selected',
        variant: 'default'
      });
      return;
    }

    // Settle every removal independently. A sequential loop aborted on the first
    // refusal, leaving the batch half-done, dropping the reason, and then reporting the
    // ATTEMPTED count — so it claimed "Removed 7" after removing 3. The counts must
    // always reconcile: a bulk action that quietly does fewer than it promised is the
    // failure mode to avoid.
    const outcomes = await Promise.allSettled(
      unassignedKeys.map(async key => {
        const { vin, campaignNumber } = parseRecallKey(key);
        await removeRecall(vin, campaignNumber);
        return key;
      })
    );

    const removedKeys = outcomes.flatMap(outcome =>
      outcome.status === 'fulfilled' ? [outcome.value] : []
    );
    const failures = outcomes.flatMap(outcome =>
      outcome.status === 'rejected' ? [outcome.reason as unknown] : []
    );

    // Only deselect what actually went — anything kept back stays visible and selected,
    // so the user can see what the message is talking about.
    if (removedKeys.length > 0) {
      setSelectedRecallKeys(prev => {
        const newSet = new Set(prev);
        removedKeys.forEach(key => newSet.delete(key));
        return newSet;
      });
    }

    const removed = removedKeys.length;
    const kept = failures.length;

    if (kept === 0) {
      toast({
        title: 'Recalls Cleared',
        description: `Removed ${removed} recall${removed !== 1 ? 's' : ''} from the assignment list`,
        variant: 'success'
      });
    } else {
      // Name the reason from the first refusal — they are almost always the same reason,
      // and one specific sentence beats a generic "some recalls could not be removed".
      const { description } = describeActionError(failures[0], 'removing these recalls');
      toast({
        title:
          removed > 0
            ? `Removed ${removed} of ${unassignedKeys.length}`
            : `Kept ${kept} recall${kept !== 1 ? 's' : ''}`,
        description: `${kept} ${kept !== 1 ? 'were' : 'was'} kept back. ${description}`,
        variant: removed > 0 ? 'default' : 'destructive'
      });
    }

    logUserActivity({
      category: 'routing',
      action: 'clear_selected_unassigned',
      entityType: 'route',
      itemCount: removed,
      metadata: {
        cleared_count: removed,
        kept_count: kept,
        attempted_count: unassignedKeys.length
      }
    });
  }, [selectedRecallKeys, assignments, removeRecall, toast]);

  // Handle bulk unassign (remove selected assignments from technician)
  const handleBulkUnassign = useCallback(
    async (assignmentIds: string[]) => {
      if (assignmentIds.length === 0) return;

      try {
        // Remove from current selection
        setSelectedRecallKeys(prev => {
          const newSet = new Set(prev);
          assignmentIds.forEach(id => {
            const assignment = assignments.find(a => a.id === id);
            if (assignment) {
              const key = `${assignment.vin}|${assignment.campaign_number}`;
              newSet.delete(key);
            }
          });
          return newSet;
        });

        // Delete all selected assignments
        await Promise.all(assignmentIds.map(id => deleteAssignment(id)));

        // Refresh data
        await refreshAssignments();
        await refetchRoute();

        // Invalidate all route queries so other pages get fresh data
        queryClient.invalidateQueries({ queryKey: routeKeys.all });

        toast({
          title: 'Recalls Unassigned',
          description: `${assignmentIds.length} recall${assignmentIds.length !== 1 ? 's' : ''} unassigned`
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to unassign some recalls',
          variant: 'destructive'
        });
      }
    },
    [assignments, deleteAssignment, refreshAssignments, refetchRoute, toast, queryClient]
  );

  // Handle bulk assignment
  const handleBulkAssign = useCallback(async () => {
    if (!selectedTechnician) return;
    // Allow proceeding if start location is dirty (to update starting point), even with no recalls
    if (selectedRecallKeys.size === 0 && !isStartLocationDirty) return;

    // Get recall data for selected keys from the merged list
    const recallsToAssign = Array.from(selectedRecallKeys).map(key => {
      const { vin, campaignNumber } = parseRecallKey(key);
      const recall = recallsForList.find(
        r => r.vin === vin && r.campaign_number === campaignNumber
      );
      return {
        vin,
        campaign_number: campaignNumber,
        recall_title: recall?.recall_title,
        dealer_name: recall?.dealer_name,
        dealer_id: recall?.dealer_id,
        dealer_city: recall?.dealer_city,
        dealer_region: recall?.dealer_region,
        dealer_address: recall?.dealer_address,
        dealer_phone: recall?.dealer_phone,
        year: recall?.year,
        make: recall?.make,
        model: recall?.model,
        // Manual stop fields
        stop_type: recall?.stop_type,
        manual_stop_reason: recall?.manual_stop_reason,
        manual_stop_lat: recall?.dealer_latitude,
        manual_stop_lng: recall?.dealer_longitude,
        // Pass manual stop notes to assignment notes field
        notes: recall?.manual_stop_notes
      };
    });

    // Format the selected date for the assignment
    const expectedVisitDate = format(selectedDate, 'yyyy-MM-dd');

    // Build start location based on current UI selection
    let startLocation: {
      type: 'custom' | 'tenant_default';
      address: string;
      display_name?: string;
      lat: number;
      lng: number;
    };
    if (
      currentTechLocationSettings.mode === 'custom' &&
      currentTechLocationSettings.customLocation
    ) {
      const customLoc = currentTechLocationSettings.customLocation;
      startLocation = {
        type: 'custom',
        address: customLoc.displayName,
        display_name: customLoc.displayName,
        lat: customLoc.lat,
        lng: customLoc.lng
      };
    } else {
      startLocation = {
        type: 'tenant_default',
        address: tenantName || '',
        lat: tenantLocation?.lat || 0,
        lng: tenantLocation?.lng || 0
      };
    }

    // eslint-disable-next-line no-console
    console.log(
      '[handleBulkAssign] startLocation being passed to bulkAssignRecalls:',
      startLocation
    );

    // Perform bulk assignment with the scheduled date and start location
    await bulkAssignRecalls(
      selectedTechnician.id,
      selectedTechnician.full_name,
      recallsToAssign,
      expectedVisitDate,
      startLocation
    );

    // Clear selection after successful assignment
    setSelectedRecallKeys(new Set());
  }, [
    selectedTechnician,
    selectedRecallKeys,
    recallsForList,
    selectedDate,
    bulkAssignRecalls,
    currentTechLocationSettings,
    tenantLocation,
    tenantName,
    isStartLocationDirty
  ]);

  // Keyboard shortcut for assignment
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && selectedRecallKeys.size > 0 && selectedTechnician && !isAssigning) {
        e.preventDefault();
        handleBulkAssign();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRecallKeys.size, selectedTechnician, isAssigning, handleBulkAssign]);

  // Internal function to create the manual stop assignment
  const createManualStopAssignment = useCallback(
    async (data: ManualStopData) => {
      if (!selectedTechnician) return;

      try {
        const expectedVisitDate = format(selectedDate, 'yyyy-MM-dd');

        await createTechnicianAssignment({
          technician_id: selectedTechnician.id,
          // Manual stops may not have VIN
          vin: data.vin || '',
          campaign_number: data.campaign_number || '',
          recall_title: data.recall_title || `${data.manual_stop_reason} @ ${data.dealer_name}`,
          dealer_name: data.dealer_name,
          dealer_address: data.dealer_address,
          dealer_city: data.dealer_city,
          dealer_region: data.dealer_region,
          ...(data.dealer_postal_code && { dealer_postal_code: data.dealer_postal_code }),
          ...(data.phone && { dealer_phone: data.phone }),
          // Vehicle info from NHTSA decode
          ...(data.vehicle_year && { vehicle_year: data.vehicle_year }),
          ...(data.vehicle_make && { vehicle_make: data.vehicle_make }),
          ...(data.vehicle_model && { vehicle_model: data.vehicle_model }),
          // Manual stop specific fields
          stop_type: 'manual',
          manual_stop_reason: data.manual_stop_reason,
          manual_stop_lat: data.lat,
          manual_stop_lng: data.lng,
          // Standard fields
          expected_visit_date: expectedVisitDate,
          ...(data.notes && { notes: data.notes }),
          ...(user?.id && { assigned_by: user.id }),
          assigned_by_name:
            user?.user_metadata?.['full_name'] || user?.email?.split('@')[0] || 'Unknown'
        });

        const stopDescription =
          `Added ${data.manual_stop_reason} at ${data.dealer_name} ` +
          `to ${selectedTechnician.full_name}'s route.`;
        toast({ title: 'Manual stop added', description: stopDescription });

        // Refresh data
        await refreshAssignments();

        // Sync to route table so manual stop appears on map and route is re-optimized
        await syncToRouteTable(selectedTechnician.id, expectedVisitDate);

        await refetchRoute();

        // Log activity for manual stop creation
        logUserActivity({
          category: 'routing',
          action: 'create_assignment',
          entityType: 'manual_stop',
          entityId: selectedTechnician.id,
          metadata: { reason: data.manual_stop_reason, dealer_name: data.dealer_name }
        });
      } catch (error) {
        console.error('[createManualStopAssignment] Error:', error);
        toast({
          title: 'Failed to add manual stop',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive'
        });
      }
    },
    [
      selectedTechnician,
      selectedDate,
      user,
      toast,
      refreshAssignments,
      refetchRoute,
      syncToRouteTable
    ]
  );

  // Handle duplicate location dialog choice
  const handleDuplicateChoice = useCallback(
    async (choice: DuplicateLocationChoice) => {
      setDuplicateDialogOpen(false);

      if (choice === 'cancel' || !pendingManualStop) {
        setPendingManualStop(null);
        setNearbyStopInfo(null);
        return;
      }

      // Both 'merge' and 'separate' proceed with creation
      // The merge behavior is handled by buildRouteDataFromAssignments proximity grouping
      await createManualStopAssignment(pendingManualStop);

      setPendingManualStop(null);
      setNearbyStopInfo(null);
    },
    [pendingManualStop, createManualStopAssignment]
  );

  // Loading state
  const isLoading = recallsLoading || assignmentsLoading;

  if (isLoading) {
    return (
      <div className='h-full flex items-center justify-center'>
        <div className='flex flex-col items-center gap-4'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
          <p className='text-muted-foreground'>Loading routing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='h-full flex flex-col pt-2 px-4 lg:px-6 gap-4'>
      {/* Page title removed — the left nav labels the page */}

      {/* Read-only banner for technicians */}
      {!canAssign && (
        <Alert className='border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20'>
          <Eye className='h-4 w-4' />
          <AlertTitle>View Only Mode</AlertTitle>
          <AlertDescription>
            You can view routes and print, but cannot assign technicians.
          </AlertDescription>
        </Alert>
      )}

      {/* Technician & Schedule Selection */}
      <TechnicianDropdown
        technicians={activeTechnicians}
        assignments={assignments}
        selectedTechnicianId={selectedTechnicianId}
        onTechnicianChange={handleTechnicianChange}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        filterMode={filterMode}
        onFilterModeChange={setFilterMode}
        showPrintButton={!!(selectedTechnician && currentRoute && currentRoute.stops.length > 0)}
        onPrintRoute={handlePrintRoute}
      />

      {/* Main Content - Split Layout - use absolute positioning to prevent content from affecting container height */}
      <div className='flex-1 relative min-h-0'>
        <div className='absolute inset-0 flex gap-4'>
          {/* Left Panel - Recall List (55%) */}
          <div className='w-[55%] h-full overflow-hidden'>
            <RecallAssignmentList
              recalls={recallsForList}
              assignments={assignments}
              selectedRecallKeys={selectedRecallKeys}
              onSelectionChange={handleSelectionChange}
              {...(canAssign && { onUnassign: handleUnassign })}
              {...(canAssign && { onRemoveRecall: handleRemoveRecall })}
              {...(canAssign && { onBulkAssign: handleBulkAssign })}
              {...(canAssign && { onBulkUnassign: handleBulkUnassign })}
              {...(canAssign && { onClearAllFromTech: handleClearAllFromTech })}
              {...(canAssign && { onClearSelectedUnassigned: handleClearSelectedUnassigned })}
              isAssigning={isAssigning}
              technicianName={selectedTechnician?.full_name || null}
              techAssignedCount={
                assignments.filter(
                  a =>
                    a.technician_id === selectedTechnician?.id &&
                    a.assignment_status !== 'completed' &&
                    a.assignment_status !== 'cancelled'
                ).length
              }
              filterMode={filterMode}
              selectedDate={selectedDate}
              loading={recallsLoading}
              className='h-full'
              allowReassignment={canAssign}
              selectedTechnicianId={selectedTechnician?.id || null}
              startLocationDirty={isStartLocationDirty}
            />
          </div>

          {/* Right Panel - Route Preview (45%) - height independent of left panel */}
          <div className='w-[45%] h-full overflow-hidden'>
            <RoutePreviewPanel
              selectedTechnician={selectedTechnician}
              assignments={assignments}
              recalls={recallsForList}
              baseLocation={tenantLocation}
              baseLocationName={tenantName}
              selectedRecallKeys={selectedRecallKeys}
              selectedDate={selectedDate}
              className='h-full'
              locationMode={currentTechLocationSettings.mode}
              onLocationModeChange={handleLocationModeChange}
              customLocation={currentTechLocationSettings.customLocation}
              onCustomLocationChange={handleCustomLocationChange}
              onRouteChange={handleRouteChange}
              storedRoute={storedRoute}
            />
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Unassign/Remove */}
      <Dialog open={confirmAction !== null} onOpenChange={open => !open && setConfirmAction(null)}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === 'unassign' ? 'Unassign Recall?' : 'Remove Recall?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'unassign'
                ? 'Are you sure you want to unassign this recall from the technician?'
                : 'Are you sure you want to remove this recall from your assignment list?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleConfirmAction}>
              {confirmAction?.type === 'unassign' ? 'Unassign' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Location Dialog */}
      {nearbyStopInfo && pendingManualStop && (
        <DuplicateLocationDialog
          open={duplicateDialogOpen}
          onOpenChange={setDuplicateDialogOpen}
          nearbyStop={nearbyStopInfo}
          newStopName={pendingManualStop.dealer_name}
          onChoice={handleDuplicateChoice}
        />
      )}
    </div>
  );
};

export default UnifiedRoutingPage;
